import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { contactApi } from '@/services/api'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

export default function CreateContact() {
  const { clientId, contactId } = useParams<{ clientId: string; contactId: string }>()
  const navigate = useNavigate()
  const isEdit = !!contactId

  const [form, setForm] = useState({
    name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    address_line1: '',
    address_line2: '',
    city: '',
    region: '',
    postal_code: '',
    country: '',
    tax_number: '',
    bank_account_name: '',
    bank_account_number: '',
    bank_sort_code: '',
    default_currency: '',
    is_customer: false,
    is_supplier: false,
    payment_terms_days: 30,
    notes: '',
  })
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(isEdit)

  useEffect(() => {
    if (!isEdit || !contactId) return
    contactApi.get(contactId)
      .then((res) => {
        const c = res.data.contact
        setForm({
          name: c.name,
          first_name: c.first_name,
          last_name: c.last_name,
          email: c.email,
          phone: c.phone,
          mobile: c.mobile,
          address_line1: c.address_line1,
          address_line2: c.address_line2,
          city: c.city,
          region: c.region,
          postal_code: c.postal_code,
          country: c.country,
          tax_number: c.tax_number,
          bank_account_name: c.bank_account_name,
          bank_account_number: c.bank_account_number,
          bank_sort_code: c.bank_sort_code,
          default_currency: c.default_currency,
          is_customer: c.is_customer,
          is_supplier: c.is_supplier,
          payment_terms_days: c.payment_terms_days,
          notes: c.notes,
        })
      })
      .catch(() => toast.error('Failed to load contact'))
      .finally(() => setLoading(false))
  }, [contactId, isEdit])

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name.trim()) {
      toast.error('Contact name is required')
      return
    }
    setSaving(true)
    try {
      const payload = { ...form, client_ledger_id: clientId }
      if (isEdit && contactId) {
        await contactApi.update(contactId, payload)
        toast.success('Contact updated')
      } else {
        await contactApi.create(payload)
        toast.success('Contact created')
      }
      navigate(`/clients/${clientId}/contacts`)
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to save contact'
      )
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">
            {isEdit ? 'Edit Contact' : 'New Contact'}
          </h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">
            {isEdit ? 'Update contact details' : 'Add a customer or supplier'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Save Contact'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate(`/clients/${clientId}/contacts`)}>
            Cancel
          </Button>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-5">
        {/* Contact Details */}
        <Card>
          <CardHeader className="border-b px-5 py-4">
            <CardTitle className="text-[13px] font-semibold text-[#000A1E]">Contact Details</CardTitle>
          </CardHeader>
          <CardContent className="p-5 grid grid-cols-2 gap-5">
            <div className="col-span-2 space-y-1.5">
              <Label>Business / Organization Name <span className="text-red-500">*</span></Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Company or contact name"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>First Name</Label>
              <Input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                placeholder="First name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Last Name</Label>
              <Input
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                placeholder="Last name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="contact@example.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone number"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Mobile</Label>
              <Input
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                placeholder="Mobile number"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Payment Terms (days)</Label>
              <Input
                type="number"
                min="0"
                value={form.payment_terms_days}
                onChange={(e) => setForm({ ...form, payment_terms_days: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="col-span-2 flex gap-6">
              <label className="flex items-center gap-2 text-[13px] text-[#000A1E]">
                <input
                  type="checkbox"
                  checked={form.is_customer}
                  onChange={(e) => setForm({ ...form, is_customer: e.target.checked })}
                  className="rounded border-gray-300"
                />
                Customer
              </label>
              <label className="flex items-center gap-2 text-[13px] text-[#000A1E]">
                <input
                  type="checkbox"
                  checked={form.is_supplier}
                  onChange={(e) => setForm({ ...form, is_supplier: e.target.checked })}
                  className="rounded border-gray-300"
                />
                Supplier
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Address & Financial */}
        <Card>
          <CardHeader className="border-b px-5 py-4">
            <CardTitle className="text-[13px] font-semibold text-[#000A1E]">Address & Financial</CardTitle>
          </CardHeader>
          <CardContent className="p-5 grid grid-cols-2 gap-5">
            <div className="col-span-2 space-y-1.5">
              <Label>Address Line 1</Label>
              <Input
                value={form.address_line1}
                onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
                placeholder="Street address"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Address Line 2</Label>
              <Input
                value={form.address_line2}
                onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
                placeholder="Suite, unit, floor"
              />
            </div>
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Region / State</Label>
              <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Postal Code</Label>
              <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Country</Label>
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="e.g. US, AU, GB" maxLength={3} />
            </div>
            <div className="space-y-1.5">
              <Label>Tax Number (ABN/TIN/VAT)</Label>
              <Input value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Default Currency</Label>
              <Input value={form.default_currency} onChange={(e) => setForm({ ...form, default_currency: e.target.value })} placeholder="e.g. USD, AUD" maxLength={3} />
            </div>
            <div className="space-y-1.5">
              <Label>Bank Account Name</Label>
              <Input value={form.bank_account_name} onChange={(e) => setForm({ ...form, bank_account_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Bank Account Number</Label>
              <Input value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Bank Sort Code / BSB</Label>
              <Input value={form.bank_sort_code} onChange={(e) => setForm({ ...form, bank_sort_code: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="input"
                rows={3}
                placeholder="Internal notes about this contact"
              />
            </div>
          </CardContent>
        </Card>

        {/* Bottom actions */}
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Save Contact'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate(`/clients/${clientId}/contacts`)}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  )
}
